import type { ActionEvidence, ActionType } from '@0xtorch/core'
import {
  type ActionError,
  type HistoryPageSortType,
  isHistoryPageSortType,
} from '@pkg/basic'
import { Outlet, createFileRoute } from '@tanstack/react-router'

const textFilterOperators = [
  'eq',
  'like',
  'not-like',
  'is-empty',
  'is-not-empty',
] as const
type TextFilterOperator = (typeof textFilterOperators)[number]

const inArrayOperators = ['in-array', 'not-in-array'] as const
type InArrayOperator = (typeof inArrayOperators)[number]

const mathFilterOperators = ['eq', 'gte', 'lte'] as const
type MathFilterOperator = (typeof mathFilterOperators)[number]

const transferOperators = [
  'in-array',
  'not-in-array',
  'in-array-from',
  'not-in-array-from',
  'in-array-to',
  'not-in-array-to',
] as const
type TransferOperator = (typeof transferOperators)[number]

const parseString = (value: unknown) =>
  typeof value === 'string' ? value : undefined

const parseStringArray = (value: unknown) =>
  Array.isArray(value) ? value : typeof value === 'string' ? [value] : undefined

const parseNumberArray = (value: unknown): number[] | undefined => {
  const values = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? [value]
      : undefined

  if (values === undefined) {
    return undefined
  }

  const numbers = values
    .map((item) => parseNumber(item))
    .filter((item): item is number => item !== undefined)

  return numbers.length === 0 ? undefined : numbers
}

const parseNumberArrayWithEmpty = (value: unknown): number[] | undefined => {
  const values = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? [value]
      : undefined

  if (values === undefined) {
    return undefined
  }

  return values
    .map((item) => parseNumber(item))
    .filter((item): item is number => item !== undefined)
}

const parseEnum = <T extends readonly string[]>(
  value: unknown,
  allowed: T,
): T[number] | undefined => {
  if (typeof value !== 'string' || !allowed.includes(value)) {
    return undefined
  }

  return value as T[number]
}

const parseNumber = (value: unknown) => {
  const parsed = Number.parseInt(value as string)
  return Number.isNaN(parsed) ? undefined : parsed
}

/**
 * Action Sheetのorderを厳密に解析する。
 * @param value URL searchから受け取った値。
 * @returns 未指定時はundefined、正しい非負整数はnumber、不正値はNaN。
 */
export const parseActionSheetOrder = (value: unknown): number | undefined => {
  if (value === undefined) {
    return undefined
  }
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) && value >= 0 ? value : Number.NaN
  }
  if (
    typeof value !== 'string' ||
    !/^(0|[1-9]\d*)$/.test(value) ||
    !Number.isSafeInteger(Number(value))
  ) {
    return Number.NaN
  }
  return Number(value)
}

const parseTrueBoolean = (value: unknown) =>
  value === true || (typeof value === 'string' && value === 'true')
    ? true
    : undefined

/**
 * History配下で共有するURL searchを検証する。
 * @param search Routerが受け取った未検証search。
 * @returns History配下で利用可能なsearch。
 */
export const validateHistorySearch = (
  search: Record<string, unknown>,
): {
  hf_account?: number[]
  hf_account_op?: InArrayOperator
  hf_amount?: string
  hf_amount_op?: MathFilterOperator
  hf_app?: string[]
  hf_app_op?: 'in-array' | 'not-in-array'
  hf_asset?: string[]
  hf_asset_op?: InArrayOperator
  hf_category?: string[]
  hf_category_op?: InArrayOperator
  hf_file?: number[]
  hf_file_op?: InArrayOperator
  hf_comment?: string
  hf_comment_op?: TextFilterOperator
  hf_cross?: string
  hf_cross_op?: TextFilterOperator
  hf_error?: ActionError[]
  hf_error_op?: InArrayOperator
  hf_evidence?: ActionEvidence[]
  hf_evidence_op?: InArrayOperator
  hf_evm_function?: string
  hf_evm_function_op?: TextFilterOperator
  hf_evm_to?: string
  hf_evm_to_op?: TextFilterOperator
  hf_loan?: string
  hf_loan_op?: TextFilterOperator
  hf_transfer_in?: string
  hf_transfer_in_op?: MathFilterOperator
  hf_transfer_out?: string
  hf_transfer_out_op?: MathFilterOperator
  hf_transfer?: string[]
  hf_transfer_op?: TransferOperator
  hf_sub_type?: number[]
  hf_type?: ActionType[]
  hf_type_op?: InArrayOperator
  hf_ts_from?: string
  hf_ts_to?: string
  hs_asc?: boolean
  hs_desc?: boolean
  hs_sort?: HistoryPageSortType
  p?: number
  s?: number
  vs?: string
  vo?: number
} => {
  return {
    hf_account: parseNumberArrayWithEmpty(search.hf_account),
    hf_account_op: parseEnum(search.hf_account_op, inArrayOperators),
    hf_amount: parseString(search.hf_amount),
    hf_amount_op: parseEnum(search.hf_amount_op, mathFilterOperators),
    hf_app: parseStringArray(search.hf_app),
    hf_app_op: parseEnum(search.hf_app_op, inArrayOperators),
    hf_asset: parseStringArray(search.hf_asset),
    hf_asset_op: parseEnum(search.hf_asset_op, inArrayOperators),
    hf_category: parseStringArray(search.hf_category),
    hf_category_op: parseEnum(search.hf_category_op, inArrayOperators),
    hf_file: parseNumberArrayWithEmpty(search.hf_file),
    hf_file_op: parseEnum(search.hf_file_op, inArrayOperators),
    hf_comment: parseString(search.hf_comment),
    hf_comment_op: parseEnum(search.hf_comment_op, textFilterOperators),
    hf_cross: parseString(search.hf_cross),
    hf_cross_op: parseEnum(search.hf_cross_op, textFilterOperators),
    hf_error: parseStringArray(search.hf_error) as ActionError[] | undefined,
    hf_error_op: parseEnum(search.hf_error_op, inArrayOperators),
    hf_evidence: parseStringArray(search.hf_evidence) as
      | ActionEvidence[]
      | undefined,
    hf_evidence_op: parseEnum(search.hf_evidence_op, inArrayOperators),
    hf_evm_function: parseString(search.hf_evm_function),
    hf_evm_function_op: parseEnum(
      search.hf_evm_function_op,
      textFilterOperators,
    ),
    hf_evm_to: parseString(search.hf_evm_to),
    hf_evm_to_op: parseEnum(search.hf_evm_to_op, textFilterOperators),
    hf_loan: parseString(search.hf_loan),
    hf_loan_op: parseEnum(search.hf_loan_op, textFilterOperators),
    hf_transfer_in: parseString(search.hf_transfer_in),
    hf_transfer_in_op: parseEnum(search.hf_transfer_in_op, mathFilterOperators),
    hf_transfer_out: parseString(search.hf_transfer_out),
    hf_transfer_out_op: parseEnum(
      search.hf_transfer_out_op,
      mathFilterOperators,
    ),
    hf_transfer: parseStringArray(search.hf_transfer),
    hf_transfer_op: parseEnum(search.hf_transfer_op, transferOperators),
    hf_sub_type: parseNumberArray(search.hf_sub_type),
    hf_type: Array.isArray(search.hf_type)
      ? search.hf_type
      : typeof search.hf_category === 'string'
        ? [search.hf_category]
        : undefined,
    hf_type_op: parseEnum(search.hf_type_op, inArrayOperators),
    hf_ts_from: (search.hf_ts_from as string) ?? undefined,
    hf_ts_to: (search.hf_ts_to as string) ?? undefined,
    hs_asc: parseTrueBoolean(search.hs_asc),
    hs_desc: parseTrueBoolean(search.hs_desc),
    hs_sort: (() => {
      const value = parseString(search.hs_sort)

      return value !== undefined && isHistoryPageSortType(value)
        ? value
        : undefined
    })(),
    p: parseNumber(search.p),
    s: parseNumber(search.s),
    vs: parseString(search.vs),
    vo: parseActionSheetOrder(search.vo),
  }
}

export const Route = createFileRoute('/_layoutPortfolio/history/')({
  validateSearch: validateHistorySearch,
  component: RouteComponent,
})

function RouteComponent() {
  return <Outlet />
}
